import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ShoppingBag, Plus, Minus, Trash2, X } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { useNavigate } from "react-router-dom";

const CartDrawer = () => {
  const { items, removeFromCart, updateQuantity, totalItems, subtotal } = useCart();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      const timer = setTimeout(() => setMounted(false), 400);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (mounted && open) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });
    } else {
      setAnimateIn(false);
    }
  }, [mounted, open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  const drawer = mounted
    ? createPortal(
        <div className="fixed inset-0 z-[9999]" style={{ fontFamily }}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{ opacity: animateIn ? 1 : 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)" }}
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div
            className="absolute top-0 right-0 h-full w-full max-w-[420px] flex flex-col transition-transform duration-300 ease-out"
            style={{
              transform: animateIn ? "translateX(0)" : "translateX(100%)",
              background: "#fefcfd",
              boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-5 shrink-0"
              style={{
                background: "linear-gradient(135deg, #E88CA4 0%, #d4728e 100%)",
              }}
            >
              <div className="flex items-center gap-3">
                <ShoppingBag size={20} className="text-white/90" />
                <h2 className="text-lg font-bold text-white tracking-wide">
                  {t("cart.title") !== "cart.title" ? t("cart.title") : "Your Bag"}
                </h2>
                {totalItems > 0 && (
                  <span className="ml-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/25 text-xs font-bold text-white">
                    {totalItems}
                  </span>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 transition-colors hover:bg-white/20"
              >
                <X size={18} className="text-white" />
              </button>
            </div>

            {/* Items */}
            <div
              className="flex-1 overflow-y-auto px-5 py-4"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#e8c0cc transparent",
              }}
            >
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                  <div className="rounded-full p-6" style={{ background: "rgba(232,140,164,0.08)" }}>
                    <ShoppingBag size={40} style={{ color: "rgba(232,140,164,0.4)" }} />
                  </div>
                  <p className="text-sm font-medium" style={{ color: "#a8405c" }}>
                    {t("cart.empty") !== "cart.empty" ? t("cart.empty") : "Your bag is empty"}
                  </p>
                  <p className="text-xs max-w-[200px]" style={{ color: "#c9a0ac" }}>
                    {t("cart.emptyHint") !== "cart.emptyHint" ? t("cart.emptyHint") : "Browse our collection and add your favorites."}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {items.map(({ product, quantity }) => (
                    <div
                      key={product.id}
                      className="flex gap-4 rounded-2xl p-3 transition-colors"
                      style={{ background: "#fdf2f5" }}
                    >
                      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-white">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt={product.title} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <ShoppingBag size={24} style={{ color: "#E88CA4" }} />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 flex-col justify-between min-w-0">
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#c97a90" }}>
                            {product.brand}
                          </p>
                          <p className="text-sm font-bold leading-tight mt-0.5 truncate" style={{ color: "#3d2c33" }}>
                            {product.title}
                          </p>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => updateQuantity(product.id, quantity - 1)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg border transition-colors hover:bg-white"
                              style={{ borderColor: "#e8d0d8" }}
                            >
                              <Minus size={12} style={{ color: "#d4728e" }} />
                            </button>
                            <span className="w-7 text-center text-sm font-bold" style={{ color: "#3d2c33" }}>
                              {quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(product.id, quantity + 1)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg border transition-colors hover:bg-white"
                              style={{ borderColor: "#e8d0d8" }}
                            >
                              <Plus size={12} style={{ color: "#d4728e" }} />
                            </button>
                          </div>

                          <span className="text-sm font-bold" style={{ color: "#d4728e" }}>
                            {(product.salePrice * quantity).toFixed(2)} QAR
                          </span>

                          <button
                            onClick={() => removeFromCart(product.id)}
                            className="rounded-lg p-1.5 transition-colors hover:bg-red-50"
                          >
                            <Trash2 size={14} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="shrink-0 px-6 py-5" style={{ borderTop: "1px solid #f5eef0" }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium" style={{ color: "#9a8a90" }}>
                    {t("cart.subtotal") !== "cart.subtotal" ? t("cart.subtotal") : "Subtotal"}
                  </span>
                  <span className="text-lg font-bold" style={{ color: "#3d2c33" }}>
                    {subtotal.toFixed(2)} QAR
                  </span>
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full rounded-full py-3.5 text-sm font-bold text-white transition-all duration-300 hover:shadow-lg active:scale-[0.98]"
                  style={{
                    background: "linear-gradient(135deg, #E88CA4, #d4728e)",
                    boxShadow: "0 4px 16px rgba(232,140,164,0.35)",
                  }}
                >
                  {t("cart.checkout") !== "cart.checkout" ? t("cart.checkout") : "Checkout"}
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 rounded-full transition-all duration-200 hover:bg-[#E88CA4]/10 active:scale-[0.95]"
        aria-label="Open cart"
      >
        <ShoppingBag size={20} strokeWidth={1.8} />
        {totalItems > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full text-[10px] font-bold text-white"
            style={{ background: "#E88CA4" }}
          >
            {totalItems}
          </span>
        )}
      </button>
      {drawer}
    </>
  );
};

export default CartDrawer;
